import { BleClient } from '@capacitor-community/bluetooth-le';
import { DirectConnectBaseDevice, BLE_SERVICES, VisibleDevice } from './devices/base/directConnectBaseDevice';
import { InitDirectConnectDeviceTypes } from './devices/base/registry';
import { ref, shallowRef, watch, watchEffect } from 'vue';
import { useUser } from '../user.store';
import { useDeviceLocation } from '@/composables/use-device-location';

const visibleDevices = shallowRef<DirectConnectBaseDevice[]>([]);
const selected = shallowRef<DirectConnectBaseDevice>();
const seenDeviceIds: { [deviceId: number]: number } = {};
const scanningIntervalOn = ref(false);
const isActivelyScanning = ref(false);
const { loadingLocation, location: selectedLocation } = useDeviceLocation(() => selected.value?.deviceId);

watchEffect(() => console.log('%c Visible Devices', 'color: magenta', visibleDevices.value));

export function useDirectConnect() {
  const user = useUser();
  InitDirectConnectDeviceTypes();
  let scanInterval: NodeJS.Timeout;
  async function performScanNow() {
    if (selected.value) {
      console.log('%c skipping performScanNow() because we are already connected to a device', 'color: magenta');
    }
    console.log('%c performScanNow()', 'color: magenta');
    if (isActivelyScanning.value) {
      return;
    }
    isActivelyScanning.value = true;
    try {
      await BleClient.requestLEScan({ allowDuplicates: true }, (res) => {
        const visibleDevice = DirectConnectBaseDevice.CreateVisibleDevice(res);
        if (!visibleDevice) {
          return;
        }
        seenDeviceIds[visibleDevice.deviceId] = 3;
        const index = visibleDevices.value.findIndex((d) => d.deviceId === visibleDevice.deviceId);
        if (index !== -1) {
          visibleDevices.value[index].update(visibleDevice);
        } else {
          const device = DirectConnectBaseDevice.Create(visibleDevice, user.useMetric);
          if (device) {
            visibleDevices.value = [...visibleDevices.value, device].sort(
              (a, b) => b.bleSignalStrength.value - a.bleSignalStrength.value
            );
          }
        }
      });
    } catch (e) {
      console.log(e);
    }
    await new Promise((resolve) => setTimeout(resolve, 5_000));
    await BleClient.stopLEScan();
    for (const k of Object.keys(seenDeviceIds)) {
      if (seenDeviceIds[+k] <= 0) {
        delete seenDeviceIds[+k];
      } else {
        seenDeviceIds[+k] = seenDeviceIds[+k] - 1;
      }
    }
    visibleDevices.value = visibleDevices.value.filter((d) => seenDeviceIds[d.deviceId]);
    isActivelyScanning.value = false;
  }

  async function startScanInterval() {
    console.log('%c startScanning()', 'color: magenta');
    await stopScanInterval();
    if (scanInterval) {
      clearInterval(scanInterval);
    }
    scanningIntervalOn.value = true;
    scanInterval = setInterval(async () => {
      await performScanNow();
    }, 20_000);
    await performScanNow();
  }

  async function stopScanInterval() {
    console.log('%c stopScanning()', 'color: magenta');
    scanningIntervalOn.value = false;
    if (scanInterval) {
      clearInterval(scanInterval);
    }
    await BleClient.stopLEScan();
  }

  async function disconnect() {
    if (selected.value) {
      await selected.value.disconnect();
    } else {
      const devices = await BleClient.getConnectedDevices([
        BLE_SERVICES.GW301.ID,
        BLE_SERVICES.DIRECT_CONNECT.ID,
        BLE_SERVICES.NCSS_CONNECT.ID,
        BLE_SERVICES.WALK_BY.ID,
      ]);
      for (const d of devices) {
        await BleClient.disconnect(d.deviceId);
      }
    }
    selected.value = undefined;
  }

  function select(deviceId: number) {
    const device = visibleDevices.value.find((d) => d.deviceId === deviceId) as DirectConnectBaseDevice;
    selected.value = device;
    return device;
  }

  function getSelectedAsDeviceType<T extends DirectConnectBaseDevice>(
    type: new (info: VisibleDevice, useMetric: boolean) => T
  ) {
    const device = shallowRef<T>();
    watch(
      () => selected.value,
      (d) => (d instanceof type ? (device.value = d) : (device.value = undefined)),
      { immediate: true }
    );
    return device;
  }

  return {
    startScanInterval,
    stopScanInterval,
    visibleDevices,
    disconnect,
    select,
    selected,
    selectedLocation,
    loadingLocation,
    getSelectedAsDeviceType,
    scanningIntervalOn,
    performScanNow,
  };
}
